/**
 * Subscribers Page
 *
 * Displays the users subscribed to the current user, in a 3-column avatar
 * grid. Mirrors Flutter's SubscribersScreen
 * (lib/presentation/screens/subscriptions/subscribers_screen.dart).
 *
 * Data source: paginated Firestore fetch via
 * `fetchExclusiveContentSubscribersPageUseCase`. Unlike SubscriptionsPage —
 * which mirrors the global `exclusiveContent` Redux slice — subscribers are
 * server-fetched on demand because they are not maintained locally.
 *
 * Architecture: Page → useSubscribers Hook → UseCase → Service
 */

import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { FlexibleHeaderBackground } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderBackground';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { useBasePage, useGridColumnsCount } from '@/presentation/hooks';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ContentGridItemState } from '@/presentation/components/common/content/ContentGridItemState';
import { UserAvatarGridItem } from '@/presentation/components/common/user/UserAvatarGridItem';
import { Navigator } from '@/presentation/routing/Navigator';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import type { RootState } from '@/presentation/store';
import { useSubscribers } from './hooks/useSubscribers';
import { SubscribersEmptyState } from './components/SubscribersEmptyState';

export const SubscribersPage = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const { expauseLocalizations } = useExpauseLocalizations();
  const subscribersCount = useSelector(
    (state: RootState) => state.auth.user?.subscribers ?? 0,
  );
  const gridColumnsCount = useGridColumnsCount();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  const { subscribers, isLoading, isLoadingMore, hasMore, error, init, loadMore } =
    useSubscribers();

  useBasePage({
    screenKey,
    onInit: () => {
      void init();
    },
    cleanReactQueryBehaviour: 'onPop',
  });

  // Match Flutter's silently-swallowing fetch behavior (see
  // `content_subscriptions_data_source.dart:134-136`): show the empty state
  // even on first-page error so the share-profile CTA stays reachable, and
  // surface the error via a toast instead of replacing the empty content.
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleBack = () => {
    Navigator.goBack();
  };

  const handleSubscriberClick = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  const renderSubscriberItem = useCallback(
    (subscription: ExclusiveContentSubscriptionEntity) => (
      <UserAvatarGridItem
        userId={subscription.userId}
        name={subscription.name}
        onClick={handleSubscriberClick}
        qa={{ id: `subscriber-${subscription.userId}` }}
      />
    ),
    [handleSubscriberClick],
  );

  const title = expauseLocalizations.getSubscribersTitle(subscribersCount);

  const emptyListComponent = isLoading ? (
    <ContentGridItemState isLoading emptyText={l10n.subscribers.empty} />
  ) : (
    <SubscribersEmptyState screenKey={screenKey} />
  );

  // Coarse lifecycle token for the page root, derived from existing state only.
  const pageStatus = isLoading && subscribers.length === 0
    ? 'loading'
    : subscribers.length === 0
      ? 'empty'
      : 'ready';

  return (
    <FlexibleHeaderList
      qa={{ id: 'subscribers', status: pageStatus }}
      listQa={{ id: 'subscribers-list' }}
      items={subscribers}
      renderItem={renderSubscriberItem}
      getItemKey={(subscription) => subscription.userId}
      gridProps={{
        columnsCount: gridColumnsCount,
        crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
        mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
        itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
        bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
      }}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      emptyListComponent={emptyListComponent}
      backgroundColor={theme.background}
      appBarOptions={{
        title,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <FlexibleHeaderBackground
              iconPath="/assets/images/subscribers.png"
              title={title}
            />
          ),
        },
      }}
    />
  );
};
